import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { AdminHeader, Card, Btn, Modal, Input, Select, Toggle, StatusBadge, Empty, ErrorBanner, Loading } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { code: "", type: "percentage", value: 10, minOrderValue: 0, maxDiscount: null, startDate: "", expiryDate: "", usageLimit: null, active: true };

export default function AdminCoupons() {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Loaded coupons stay on screen if a refresh fails — never blanked to "0 coupons".
  const { data, loading, error, stale, reload } = useAdminData(
    () => api.get("/admin/coupons").then((r) => r.data.coupons)
  );
  const items = data ?? [];

  useEffect(() => { reload(); }, [reload]);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        ...f,
        value: Number(f.value),
        minOrderValue: Number(f.minOrderValue || 0),
        maxDiscount: f.maxDiscount ? Number(f.maxDiscount) : null,
        usageLimit: f.usageLimit ? Number(f.usageLimit) : null,
        startDate: f.startDate || null,
        expiryDate: f.expiryDate || null,
        // No idempotency key needed here: coupon codes are unique server-side, so a
        // retry after a lost response fails safely with "Coupon code already exists"
        // instead of creating a duplicate.
      };
      if (editId) await api.put(`/admin/coupons/${editId}`, payload); else await api.post("/admin/coupons", payload);
      toast.success("Saved");
      setOpen(false);          // only on success: a failed save keeps the form open
      reload();
    } catch (e) {
      setSaveError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete coupon?")) return;
    try { await api.delete(`/admin/coupons/${id}`); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <AdminHeader title="Coupons" subtitle={`${items.length} coupons`} action={<Btn variant="accent" onClick={() => { setF(EMPTY); setEditId(null); setSaveError(null); setOpen(true); }} data-testid="add-coupon-btn"><Plus className="h-4 w-4" /> Add Coupon</Btn>} />
      {error && <ErrorBanner className="mb-4" message={error.message} stale={stale} onRetry={reload} />}
      {loading && !data && !error ? <Loading text="Loading coupons…" /> : (
      <Card className="overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3">Code</th><th className="p-3">Discount</th><th className="p-3">Min Order</th><th className="p-3">Used</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50" data-testid={`coupon-${c.code}`}>
                <td className="p-3 font-mono font-semibold">{c.code}</td>
                <td className="p-3">{c.type === "percentage" ? `${c.value}%` : formatINR(c.value)}</td>
                <td className="p-3 font-mono">{formatINR(c.minOrderValue)}</td>
                <td className="p-3 font-mono">{c.usedCount}{c.usageLimit ? `/${c.usageLimit}` : ""}</td>
                <td className="p-3"><StatusBadge status={c.active ? "active" : "inactive"} /></td>
                <td className="p-3 text-right">
                  <button onClick={() => { setF({ ...c, startDate: c.startDate?.slice(0, 10) || "", expiryDate: c.expiryDate?.slice(0, 10) || "" }); setEditId(c.id); setSaveError(null); setOpen(true); }} className="p-2 text-slate-500 hover:text-amber-600" data-testid={`edit-coupon-${c.code}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(c.id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!error && !loading && items.length === 0 && <Empty />}
        {error && items.length === 0 && (
          <p className="text-xs text-slate-500 p-3" data-testid="coupons-load-failed-note">
            Coupons could not be loaded. This is not an empty list — retry above.
          </p>
        )}
      </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Coupon" : "Add Coupon"}>
        <form onSubmit={save} className="space-y-4">
          {saveError && <ErrorBanner testid="coupon-save-error" message={saveError} />}
          <Input label="Code" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} testid="coupon-code" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} testid="coupon-type">
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </Select>
            <Input label={f.type === "percentage" ? "Value (%)" : "Value (₹)"} type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} testid="coupon-value" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Order (₹)" type="number" value={f.minOrderValue} onChange={(e) => setF({ ...f, minOrderValue: e.target.value })} />
            <Input label="Max Discount (₹)" type="number" value={f.maxDiscount || ""} onChange={(e) => setF({ ...f, maxDiscount: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start Date" type="date" value={f.startDate || ""} onChange={(e) => setF({ ...f, startDate: e.target.value })} />
            <Input label="Expiry Date" type="date" value={f.expiryDate || ""} onChange={(e) => setF({ ...f, expiryDate: e.target.value })} />
          </div>
          <Input label="Usage Limit" type="number" value={f.usageLimit || ""} onChange={(e) => setF({ ...f, usageLimit: e.target.value })} />
          <Toggle label="Active" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />
          <Btn variant="accent" type="submit" className="w-full" loading={saving} data-testid="save-coupon-btn">{saveError ? "Retry Save" : "Save"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
